import React, { useState, useEffect, useRef } from 'react'
import ReactModal from 'react-modal'
import { PropTypes } from 'prop-types'
import { ReactComponent as CloseIcon } from 'images/icons/close-circle.svg'
import useFocusTrap from 'scripts/hooks/useFocusTrap'
import './styles.scss'

const MODAL_FOCUSABLE_SELECTOR = [
  '[autofocus]',
  '[data-fallback-focus]',
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

const appElement =
  typeof document !== 'undefined' ? document.querySelector('#root') : null
if (appElement) {
  ReactModal.setAppElement(appElement)
}

const focusFirstElementInModal = modalElement => {
  if (!modalElement) {
    return
  }

  const activeElement = document.activeElement
  if (activeElement && typeof activeElement.blur === 'function') {
    activeElement.blur()
  }

  const focusTarget =
    modalElement.querySelector('[data-fallback-focus]') ||
    modalElement.querySelector(MODAL_FOCUSABLE_SELECTOR)

  if (focusTarget && typeof focusTarget.focus === 'function') {
    focusTarget.focus()
    return
  }

  if (typeof modalElement.focus === 'function') {
    modalElement.focus()
  }
}

const Modal = ({
  isOpen,
  onRequestClose,
  title,
  children,
  style,
  className,
  portalClassName,
  overlayClassName,
  ariaHideApp,
  modalRoot,
  isEspiredEstatePopup,
  ...props
}) => {
  const [popupOpened, setPopupOpened] = useState(true)
  const modalRef = useRef(null)

  const actualIsOpen = isEspiredEstatePopup ? popupOpened : isOpen
  const actualOnRequestClose = isEspiredEstatePopup
    ? () => setPopupOpened(false)
    : onRequestClose

  const handleAfterOpen = () => {
    if (!modalRef.current) {
      return
    }

    focusFirstElementInModal(modalRef.current)

    requestAnimationFrame(() => {
      focusFirstElementInModal(modalRef.current)
      requestAnimationFrame(() => {
        focusFirstElementInModal(modalRef.current)
      })
    })
  }

  // Focus trap (also restores focus to document.activeElement from when the modal opened)
  useFocusTrap(actualIsOpen, modalRef)

  // Handle ESC key press
  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Escape' && actualIsOpen) {
        actualOnRequestClose()
      }
    }

    if (actualIsOpen) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [actualIsOpen, actualOnRequestClose])

  return (
    <ReactModal
      {...props}
      isOpen={actualIsOpen}
      onRequestClose={actualOnRequestClose}
      onAfterOpen={handleAfterOpen}
      className={`dhsv-modal__container ${className}`}
      portalClassName={`dhsv-modal dhsv-modal--${style} ${portalClassName}`}
      overlayClassName={`dhsv-modal__overlay ${overlayClassName}`}
      ariaHideApp={ariaHideApp}
      parentSelector={modalRoot ? () => modalRoot : undefined}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
      shouldFocusAfterRender={true}
      shouldReturnFocusAfterClose={false}
    >
      <div ref={modalRef} className="dhsv-modal__focus-scope" tabIndex="-1">
        <div className="-header">
          {title && (
            <h4 id="modal-title" className="-title">
              {title}
            </h4>
          )}

          <button
            className="-close"
            onClick={actualOnRequestClose}
            aria-label="Schließen"
            data-fallback-focus
            type="button"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="-body" data-modal-content>
          {isEspiredEstatePopup ? (
            <p>
              Vermutlich wurde die Anzeige gelöscht. Sehen Sie sich hier einfach
              neue interessante Angebote an.
            </p>
          ) : (
            children
          )}
        </div>
      </div>
    </ReactModal>
  )
}
Modal.defaultProps = {
  isOpen: false,
  style: 'default',
  className: '',
  portalClassName: '',
  overlayClassName: '',
  ariaHideApp: false,
}

Modal.propTypes = {
  isOpen: PropTypes.bool,
  onRequestClose: PropTypes.func,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.any,
  style: PropTypes.string,
  className: PropTypes.string,
  portalClassName: PropTypes.string,
  overlayClassName: PropTypes.string,
  ariaHideApp: PropTypes.bool,
  isEspiredEstatePopup: PropTypes.bool,
  modalRoot: PropTypes.instanceOf(Element),
}

export default Modal
